import {useLazyQuery} from "@apollo/client/react";
import {
    CtrDomainsVf,
    CtrGenresProprietes,
    CtrStaticTables,
    Maybe,
    OrderedPropertyType,
    Query
} from "../graphql/generated/graphql-main.tsx";
import {gqlTemplates} from "../utils/gql-client.ts";
import {coreActions} from "../store/slice-core";
import {useXDispatch, useXSelector} from "../store/hooks.ts";
import {useWpContext} from "../components/context/WordPressProvider.tsx";
import {useEffect} from "react";
import {CoreStateType} from "../store/slice-core/types.ts";
import useCtrValeursFixes from "./useCtrValeursFixes.ts";
import {PropertyTypeCodes, TSafePropertyType} from "../@types/models.ts";
import {propertyTypeGroups} from "../utils/utils.ts";
import {compareByKey, findBy} from "../utils/array-utils.ts";
import useSiteConfig from "./useSiteConfig.ts";
import useReactTexts from "./useReactTexts.ts";
import {DocumentNode} from "@apollo/client";

const set = coreActions.$setGenresProprietes;
// data type in Redux Store core slice
const dtype: keyof CoreStateType = CtrStaticTables.GenresProprietes;
// GraphQL document node to query for the data type
const gnode: DocumentNode = gqlTemplates.genresProprietes;
// GraphQL mutation result data key
const gkey: keyof Query = "ctrGenresProprietes";

export default function useCtrGenresProprietes<T extends CtrGenresProprietes>() {
    const dispatch = useXDispatch();
    const {is_fr} = useWpContext();
    const {__} = useReactTexts();
    const $entries = useXSelector(s => s.core[dtype] as T[]);
    const {fixedValue} = useCtrValeursFixes();
    const {$config} = useSiteConfig();

    // note: relies on manually-created enum from GENRES_PROPRIETES (remember to update yearly when Centris updates)
    const groupedTypes: TSafePropertyType[] = [
        {...propertyTypeGroups.home, label: __("Maison unifamiliale")},
        {...propertyTypeGroups.plex, label: __("Multiplex")}
    ];

    const [fetch, {loading}] = useLazyQuery<Query>(gnode, {fetchPolicy: "cache-and-network"});
    const load = async () => {
        const {data} = await fetch();
        if (data?.[gkey] == null) return;
        dispatch(set(data?.[gkey] as T[]));
    }

    /**
     * Given category code and actual genre code, find corresponding entry in "GENRES_PROPRIETES" static table.
     * @param category
     * @param code
     * @param args
     */
    const genreBy = (category: Maybe<string>, code: Maybe<string>, args?: { strict: boolean }): Maybe<string> => {
        const _entry = $entries.find(it =>
            it.CATEGORIE_PROPRIETE == category && it.GENRE_PROPRIETE == code);

        // GENRE_PROPRIETE sometimes is null/undefined, so default back to CATEGORIE_PROPRIETE
        if (_entry == null)
            return fixedValue(CtrDomainsVf.CategoriePropriete, category);

        // if genre was properly defined, return its label
        const _key1: keyof CtrGenresProprietes = is_fr ? "CUSTOM_FR" : "CUSTOM_EN";
        const _key2: keyof CtrGenresProprietes = is_fr ? "DESCRIPTION_FRANCAISE" : "DESCRIPTION_ANGLAISE";
        if (args?.strict)
            return _entry?.[_key2] || null;
        else
            return _entry?.[_key1] || _entry?.[_key2] || null;
    }

    /**
     * Given category code, return all {@link CtrGenresProprietes} instances that are child of this category.
     * @param category
     */
    const genresByCat = (category: Maybe<string>): T[] => {
        return $entries.filter(it => it.CATEGORIE_PROPRIETE == category) ?? [];
    }

    /**
     * Return array of {@link OrderedPropertyType}s in default label-sorted order (do further sorting as needed)
     * @param genres - depends on cases (in grid, provide a subset of 'used' genres; in admin, all genres provided)
     */
    const filterablePropertyTypes = (genres: CtrGenresProprietes[]) => {
        const _finalOptions: OrderedPropertyType[] = [];
        genres.forEach((genre, index) => {
            const _code = genre.GENRE_PROPRIETE! as PropertyTypeCodes;

            // if this genre's code is part of a grouping, add it to <option> object's internal array
            let _isPartOfGroup = false;
            groupedTypes.forEach(group => {
                // we don't know which group this code may be part of, so check both and skip if not in this group
                if (!group.value.includes(_code)) return;

                // add genre's code to option's sub-array, so selecting it will select entire group's codes
                const _option = () => findBy(_finalOptions, "key", group.key);
                if (_option() == null) _finalOptions.push({
                    key: group.key,
                    label: group.label,
                    value: []
                });
                _option()!.value!.push(_code);
                _isPartOfGroup = true;
            });
            if (_isPartOfGroup) return; // go on with next genre

            // default: all non-grouped property types
            _finalOptions.push({
                key: genre.GENRE_PROPRIETE!,
                label: genreBy(genre.CATEGORIE_PROPRIETE ?? null, genre.GENRE_PROPRIETE ?? null) ?? "",
                value: [genre.GENRE_PROPRIETE!]
            });
        });

        // apply custom ordering to each property type (stored in WebsiteConfig)
        _finalOptions.forEach(it => {
            const _customOrder = findBy($config.ptype_ordering ?? [], "key", it.key)?.order;
            it.order = _customOrder ?? 1;
        });
        // do final sorting by custom order
        return _finalOptions.toSorted(compareByKey("order"));
    }

    useEffect(() => {
        if ($entries.length > 0) return;
        load();
    }, [$entries]);

    return {
        $genres: $entries,
        loading,
        genreBy,
        genresByCat,
        filterablePropertyTypes
    }
}